import { ReactNode } from "react";
import { Link, generatePath, useLocation, useParams } from "react-router-dom";
import Layout from "@/containers/Layout";
import AccessControl, { ACCESS_LEVELS } from "@components/AccessControl";
import { Trans } from "@lingui/react/macro";
import { FEATURE } from "@/features";
import { useHasFeature } from "@/hooks/useHasFeature";
import { ROUTES } from "@/routes_new";
import { Tabs, TabsList, TabsTrigger } from "@components/ui/tabs";

import Header from "./partials/Header";
import Footer from "./partials/Footer";

type SettingsLayoutProps = {
  children: ReactNode;
};

export default function SettingsLayout({ children }: SettingsLayoutProps) {
  // URL state
  const { orgUuid } = useParams<{ orgUuid: string }>();
  const location = useLocation();

  // Helpers
  const hasApiAccess = useHasFeature(FEATURE.HAS_API_ACCESS);
  const hasSearchApiAccess = useHasFeature(FEATURE.HAS_SEARCH_API_ACCESS);
  const showApiTab = hasApiAccess || hasSearchApiAccess;

  const settingsPath = generatePath(ROUTES.SETTINGS_ORGID, { orgUuid });
  const emailPath = generatePath(ROUTES.SETTINGS_EMAIL_ORGID, { orgUuid });
  const teamPath = generatePath(ROUTES.SETTINGS_TEAM_ORGID, { orgUuid });
  const apiPath = generatePath(ROUTES.SETTINGS_API_ORGID, { orgUuid });

  const getActiveTab = () => {
    if (location.pathname.startsWith(apiPath)) return "api";
    if (location.pathname.startsWith(teamPath)) return "team";
    if (location.pathname.startsWith(emailPath)) return "email";
    return "account";
  };

  return (
    <Layout>
      <Header />

      <Tabs value={getActiveTab()} data-testid="settings-tabs-nav">
        <TabsList>
          <AccessControl level={ACCESS_LEVELS.ADMIN}>
            <TabsTrigger
              value="account"
              asChild
              data-testid="tab-heading-account"
            >
              <Link to={settingsPath}>
                <Trans>Konto</Trans>
              </Link>
            </TabsTrigger>
          </AccessControl>

          <TabsTrigger value="email" asChild data-testid="tab-heading-email">
            <Link to={emailPath}>
              <Trans>E-post preferenser</Trans>
            </Link>
          </TabsTrigger>

          <AccessControl level={ACCESS_LEVELS.ADMIN}>
            <TabsTrigger value="team" asChild data-testid="tab-heading-users">
              <Link to={teamPath}>
                <Trans>Användare</Trans>
              </Link>
            </TabsTrigger>
          </AccessControl>

          {showApiTab && (
            <TabsTrigger value="api" asChild data-testid="tab-heading-api">
              <Link to={apiPath}>
                <Trans>API-tillgång</Trans>
              </Link>
            </TabsTrigger>
          )}
        </TabsList>
      </Tabs>

      {children}

      <div className="w-full p-4 lg:p-6 max-w-7xl">
        <Footer />
      </div>
    </Layout>
  );
}
